import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, Path, Text as SvgText, G } from 'react-native-svg';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useResponsive } from '../../../hooks/useResponsive';
import { FONTS } from '../../../constants/config';
import { createShadow } from '../../../utils/styles';
import { KPIStatusDistribution } from '../types';

interface DetailStatsRowProps {
  studentsCount: number;
  teachersCount: number;
  activitiesCount: number;
  kpisCount: number;
  programsCount: number;
  sessionsCount?: number;
  overallProgress: number;
  statusDistribution: KPIStatusDistribution;
  activitiesLabel?: boolean;
  programsLabelOverride?: string;
  programsIconOverride?: string;
}

/* ── Donut Chart ──────────────────────────────────────────── */
function StatusDonut({ distribution }: { distribution: KPIStatusDistribution }) {
  const size = 140;
  const strokeWidth = 28;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;

  const total = distribution.excellent + distribution.good + distribution.needsImprovement;
  const segments = [
    { value: distribution.excellent, color: '#2ca02c' },
    { value: distribution.good, color: '#ff7f0e' },
    { value: distribution.needsImprovement, color: '#d62728' },
  ];

  let offset = 0;
  return (
    <Svg width={size} height={size}>
      {segments.map((seg, i) => {
        const pct = total > 0 ? seg.value / total : 0;
        const dash = pct * circumference;
        const gap = circumference - dash;
        const currentOffset = offset;
        offset += dash;
        if (pct === 0) return null;
        return (
          <Circle
            key={i}
            cx={center}
            cy={center}
            r={radius}
            stroke={seg.color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={-currentOffset}
            rotation={-90}
            origin={`${center}, ${center}`}
          />
        );
      })}
    </Svg>
  );
}

/* ── Progress Ring ────────────────────────────────────────── */
function ProgressRing({ percentage }: { percentage: number }) {
  const { colors } = useTheme();
  const size = 160;
  const strokeWidth = 12;
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ position: 'absolute' }}>
        <Circle
          cx={center} cy={center} r={radius}
          stroke="#E5E7EB" strokeWidth={strokeWidth} fill="none"
        />
        <Circle
          cx={center} cy={center} r={radius}
          stroke="#22C55E" strokeWidth={strokeWidth} fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          rotation={-90}
          origin={`${center}, ${center}`}
        />
      </Svg>
      <Text style={[progressStyles.pctText, { color: colors.textPrimary }]}>
        %{percentage}
      </Text>
    </View>
  );
}

/* ── Stat Card ────────────────────────────────────────────── */
function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: number;
  icon: string;
}) {
  const { colors } = useTheme();
  return (
    <View
      style={[
        statStyles.card,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
        },
        createShadow(0, 1, 3, '#000', 0.1),
      ]}
    >
      <View style={statStyles.topRow}>
        <Text style={[statStyles.label, { color: colors.textPrimary }]}>{label}</Text>
        <View style={statStyles.iconWrap}>
          <Ionicons name={icon as any} size={24} color={colors.textMuted} />
        </View>
      </View>
      <Text style={[statStyles.value, { color: colors.textPrimary }]}>{value}</Text>
    </View>
  );
}

/* ── Legend Item ───────────────────────────────────────────── */
function LegendItem({ label, value, color }: { label: string; value: number; color: string }) {
  const { colors } = useTheme();
  return (
    <View style={legendStyles.row}>
      <Text style={[legendStyles.pct, { color: '#303030' }]}>% {value}</Text>
      <View style={legendStyles.labelWrap}>
        <Text style={[legendStyles.label, { color: colors.textMuted }]}>{label}</Text>
        <View style={[legendStyles.bar, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

/* ── Main Component ───────────────────────────────────────── */
export default function DetailStatsRow({
  studentsCount,
  teachersCount,
  activitiesCount,
  kpisCount,
  programsCount,
  sessionsCount,
  overallProgress,
  statusDistribution,
  activitiesLabel,
  programsLabelOverride,
  programsIconOverride,
}: DetailStatsRowProps) {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const programsLabel = programsLabelOverride
    ? programsLabelOverride
    : activitiesLabel
      ? (isRTL ? 'الأنشطة' : 'Activities')
      : (isRTL ? 'البرامج والمشاريع' : 'Programs');

  return (
    <View style={[styles.container, isMobile && styles.containerMobile]}>
      {/* Left: Donut + Legend */}
      <View
        style={[
          styles.donutCard,
          {
            backgroundColor: colors.card,
            borderColor: colors.cardBorder,
          },
          createShadow(0, 1, 3, '#000', 0.1),
        ]}
      >
        <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
          {t('specializationDetail.statusDistribution')}
        </Text>
        <View style={styles.donutContent}>
          <StatusDonut distribution={statusDistribution} />
          <View style={styles.legendCol}>
            <LegendItem
              label={isRTL ? 'ممتاز' : 'Excellent'}
              value={statusDistribution.excellent}
              color="#2ca02c"
            />
            <LegendItem
              label={isRTL ? 'جيد' : 'Good'}
              value={statusDistribution.good}
              color="#ff7f0e"
            />
            <LegendItem
              label={isRTL ? 'يحتاج تحسين' : 'Needs improvement'}
              value={statusDistribution.needsImprovement}
              color="#d62728"
            />
          </View>
        </View>
      </View>

      {/* Center: Progress Ring */}
      <View
        style={[
          styles.progressCard,
          {
            backgroundColor: colors.card,
            borderColor: colors.cardBorder,
          },
          createShadow(0, 1, 3, '#000', 0.1),
        ]}
      >
        <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
          {t('specializationDetail.overallProgress')}
        </Text>
        <ProgressRing percentage={overallProgress} />
      </View>

      {/* Right: 4 Stat Cards in 2x2 grid */}
      <View style={styles.statsGrid}>
        <View style={styles.statsRow}>
          <StatCard
            label={isRTL ? 'مؤشرات الأداء' : 'KPIs'}
            value={kpisCount}
            icon="bar-chart-outline"
          />
          <StatCard
            label={isRTL ? 'الطلاب المشاركون' : 'Students'}
            value={studentsCount}
            icon="school-outline"
          />
        </View>
        <View style={styles.statsRow}>
          <StatCard
            label={sessionsCount !== undefined ? (isRTL ? 'الجلسات' : 'Sessions') : (programsLabelOverride || (isRTL ? 'الأنشطة' : 'Activities'))}
            value={sessionsCount !== undefined ? sessionsCount : (programsLabelOverride ? programsCount : activitiesCount)}
            icon={sessionsCount !== undefined ? 'time-outline' : (programsIconOverride || 'calendar-outline')}
          />
          <StatCard
            label={sessionsCount !== undefined ? (isRTL ? 'الأنشطة' : 'Activities') : (isRTL ? 'البرامج والمشاريع' : 'Programs')}
            value={sessionsCount !== undefined ? activitiesCount : programsCount}
            icon={sessionsCount !== undefined ? 'calendar-outline' : 'folder-outline'}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 20,
  },
  containerMobile: {
    flexDirection: 'column',
  },
  donutCard: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    gap: 20,
    alignItems: 'center',
  },
  donutContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    flex: 1,
  },
  legendCol: {
    gap: 24,
  },
  progressCard: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    gap: 20,
    alignItems: 'center',
    justifyContent: 'center',
    width: 259,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    width: '100%',
  },
  statsGrid: {
    flex: 1,
    gap: 20,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 20,
  },
});

const progressStyles = StyleSheet.create({
  pctText: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
  },
});

const statStyles = StyleSheet.create({
  card: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconWrap: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  label: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  value: {
    fontSize: 30,
    fontWeight: '700',
    textAlign: 'center',
  },
});

const legendStyles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    justifyContent: 'flex-end',
  },
  pct: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    minWidth: 50,
  },
  labelWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    justifyContent: 'flex-end',
    width: 80,
  },
  label: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'center',
  },
  bar: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
});
